import React, { useState, useEffect } from 'react';
import ClariMeLogo, { EmbraceMark } from './ClariMeLogo';
import { SparkleIcon, UsersIcon, CheckIcon } from './icons';
import { formatVisitDate, formatVisitTime } from '../utils/dateUtils';

/**
 * SharedSummaryPage
 * 
 * Public, read-only view of a visit summary.
 * Accessed via SMS link: /share/:shareId
 * 
 * No authentication required — just displays the summary.
 * Includes CTA to join the Care Group (requires creating account).
 * 
 * Supports both rich backend format (headline, familySummary,
 * warningSignals, actionItems, medication changes) and legacy mock format.
 */

// ── Small utility icons (page-specific, not worth adding to shared) ─

const CalendarIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ClockIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const UserIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const LockIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

// ── Platform detection ──────────────────────────────────────────────

function detectPlatform() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  // Inside the native app (via Universal Link) — no download CTAs make sense
  const isNativeApp = !!window.Capacitor?.isNativePlatform?.();
  return { isIOS, isAndroid, isMobile: isIOS || isAndroid, isNativeApp };
}

// ── Top-of-page App Store banner (iOS only, dismissible) ───────────
// Catches bouncers — most family members tap, skim the headline, leave.
// Position above the visit card so it's the first thing visible.

const APP_STORE_URL = 'https://apps.apple.com/app/clarime/id6759699245';
const BANNER_DISMISS_KEY = 'clarime_share_app_banner_dismissed';

function AppStoreBanner() {
  const { isIOS, isNativeApp } = detectPlatform();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(BANNER_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  if (!isIOS || isNativeApp || dismissed) return null;

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { localStorage.setItem(BANNER_DISMISS_KEY, '1'); } catch {}
    setDismissed(true);
  };

  return (
    <a
      href={APP_STORE_URL}
      className="flex items-center gap-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-2xl px-4 py-3 shadow-md shadow-teal-500/20 active:scale-[0.99] transition-all no-underline"
    >
      <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-tight">Get the ClariMe app</p>
        <p className="text-[12px] text-teal-50/90 leading-tight mt-0.5">
          Open this and future updates instantly
        </p>
      </div>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0 active:bg-white/25"
      >
        <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M2 2l10 10M12 2L2 12" />
        </svg>
      </button>
    </a>
  );
}

// ── Smart CTA block ─────────────────────────────────────────────────
// Shows different download/join links depending on platform.
// iOS  → App Store link (once published) + deep link for existing users
// Android / mobile → "Add to Home Screen" PWA prompt (web signup)
// Desktop → web signup

function SharedSummaryCTA({ careGroup, patientName, shareId }) {
  const { isIOS, isAndroid, isMobile, isNativeApp } = detectPlatform();

  // Viewing inside the native app (Universal Link) — download/join CTAs
  // don't apply; the header's "Open ClariMe" button is the way back.
  if (isNativeApp) return null;

  // Web join link — works for all platforms, stores group context through signup
  const webJoinUrl = `/#/join?group=${careGroup.id}`;

  // App Store link — placeholder until app is published
  const appStoreUrl = 'https://apps.apple.com/app/clarime/id6759699245';

  // Personal, explicit ask. The reader was added to this group by the
  // patient — say so, and say what an account gets them. "Stay in the loop"
  // was too vague to convert.
  const firstName = (patientName || '').trim().split(/\s+/)[0] || 'Your family member';

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center space-y-4">
      <EmbraceMark className="w-12 h-12 mx-auto" id="share-cta" />
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-1">
          {firstName} added you to {careGroup.name}
        </h3>
        <p className="text-slate-500 text-sm max-w-xs mx-auto">
          Create a free account and you'll get every one of {firstName}'s visit summaries automatically, the moment they're shared.
        </p>
      </div>

      {/* Primary CTA: platform-specific download / join */}
      {isIOS ? (
        <a
          href={appStoreUrl}
          className="inline-block w-full py-3.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-semibold shadow-lg shadow-teal-500/20 active:scale-[0.98] transition-all"
        >
          Get ClariMe on the App Store
        </a>
      ) : (
        <a
          href={webJoinUrl}
          className="inline-block w-full py-3.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-semibold shadow-lg shadow-teal-500/20 active:scale-[0.98] transition-all"
        >
          Join {careGroup.name}
        </a>
      )}

      {/* iOS: also offer web signup if App Store not yet live */}
      {isIOS && (
        <a
          href={webJoinUrl}
          className="block text-slate-400 text-xs underline"
        >
          Or join on the web
        </a>
      )}

      <p className="text-slate-400 text-xs">
        Free · Takes 30 seconds
      </p>
    </div>
  );
}

// ── Format date ─────────────────────────────────────────────────────

const formatDate = (dateString) => formatVisitDate(dateString);

// ── Loading skeleton ────────────────────────────────────────────────

const LoadingSkeleton = () => (
  <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4">
    <div className="animate-pulse space-y-4 w-full max-w-md">
      <div className="flex items-center gap-3 justify-center mb-8">
        <div className="w-9 h-9 bg-slate-200 rounded-xl" />
        <div className="h-7 w-24 bg-slate-200 rounded-lg" />
      </div>
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 space-y-4">
        <div className="h-5 w-3/4 bg-slate-100 rounded" />
        <div className="h-4 w-1/2 bg-slate-100 rounded" />
        <div className="space-y-2 pt-4">
          <div className="h-4 w-full bg-slate-100 rounded" />
          <div className="h-4 w-full bg-slate-100 rounded" />
          <div className="h-4 w-2/3 bg-slate-100 rounded" />
        </div>
      </div>
    </div>
  </div>
);

// ── Error state ─────────────────────────────────────────────────────

const ErrorState = ({ message }) => (
  <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6">
    <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-5">
      <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    </div>
    <h1 className="text-xl font-semibold text-slate-900 mb-2">Summary Not Found</h1>
    <p className="text-slate-500 text-center max-w-xs mb-8">
      {message || "This summary link may have expired or doesn't exist."}
    </p>
    <a
      href="/"
      className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-teal-500/20 transition-all"
    >
      Go to ClariMe
    </a>
  </div>
);

// ── Main component ──────────────────────────────────────────────────

export default function SharedSummaryPage({ shareId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
        const response = await fetch(`${apiUrl}/shares/${shareId}`);

        if (!response.ok) {
          // Surface the API's message (e.g. the 410 expired-link copy) when present
          const body = await response.json().catch(() => null);
          throw new Error(
            body?.error || (response.status === 404 ? 'Summary not found' : 'Unable to load summary')
          );
        }

        const data = await response.json();
        setSummaryData(data);
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Unable to load summary');
        setLoading(false);
      }
    };

    fetchSummary();
  }, [shareId]);

  if (loading) return <LoadingSkeleton />;
  if (error || !summaryData) return <ErrorState message={error} />;

  const { visit, patientName, careGroup } = summaryData;
  const summary = visit.summary || {};

  // Support both backend format (rich) and legacy mock format
  const keyPoints = summary.keyPoints || [];
  const medications = summary.medications || [];
  const headline = summary.headline || null;
  const familySummary = summary.familySummary || null;
  const followUp = typeof summary.followUp === 'object' ? summary.followUp : null;
  const followUpString = typeof summary.followUp === 'string' ? summary.followUp : null;
  const questions = summary.questionsToConsider || summary.questionsToAsk || [];

  const specialtyColors = {
    'Oncology': 'from-purple-500 to-violet-600',
    'Primary Care': 'from-blue-500 to-indigo-600',
    'Cardiology': 'from-red-500 to-rose-600',
    'Neurology': 'from-amber-500 to-orange-600',
    'Orthopedics': 'from-emerald-500 to-teal-600',
    'General': 'from-slate-500 to-slate-600',
  };
  const gradient = specialtyColors[visit.specialty] || specialtyColors['General'];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header bar */}
      <header className="bg-white border-b border-slate-100 px-4 py-3 pt-safe">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <ClariMeLogo size="small" />
          {detectPlatform().isNativeApp ? (
            <button
              onClick={() => { window.location.hash = '/app'; }}
              className="text-teal-600 text-sm font-medium active:text-teal-700"
            >
              Open ClariMe
            </button>
          ) : (
            <div className="flex items-center gap-1.5 text-slate-400 text-xs">
              <LockIcon />
              <span>Shared securely</span>
            </div>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="px-4 py-6 max-w-lg mx-auto space-y-5">

        {/* iOS top-of-page App Store banner — dismissible, no-op elsewhere */}
        <AppStoreBanner />

        {/* Visit header card */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}>
              <span className="text-white font-bold">
                {(visit.specialty || 'GE').slice(0, 2).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-slate-900">{patientName}'s Visit</h1>
              <p className="text-slate-600 font-medium">{visit.provider}</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  {formatDate(visit.date)}
                </span>
                {/* Visit duration is intentionally NOT shown: it invites judging
                    how long the doctor spent, and contradicts time-based billing
                    notes that count off-visit work (chart review, coordination). */}
                {visit.time && (
                  <span className="flex items-center gap-1">
                    <ClockIcon className="w-3.5 h-3.5" />
                    {formatVisitTime(visit.time)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Family summary — the quick-read version, shown first for SMS recipients */}
        {familySummary && (
          <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-2xl p-5 border border-violet-100">
            <div className="flex items-center gap-2 mb-2">
              <UsersIcon className="w-4 h-4 text-violet-500" />
              <h2 className="font-semibold text-violet-900 text-sm">Quick Summary</h2>
            </div>
            <p className="text-violet-800 leading-relaxed">{familySummary}</p>
          </div>
        )}

        {/* Headline */}
        {headline && !familySummary && (
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-5 border border-teal-100">
            <div className="flex items-start gap-3">
              <SparkleIcon className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
              <p className="text-teal-900 font-medium leading-relaxed">{headline}</p>
            </div>
          </div>
        )}

        {/* Key points */}
        {keyPoints.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <h2 className="font-semibold text-slate-900 mb-3">Key Points</h2>
            <ul className="space-y-3">
              {keyPoints.map((point, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckIcon className="w-3 h-3 text-teal-600" />
                  </div>
                  <span className="text-slate-700 leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Medications */}
        {medications.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <h2 className="font-semibold text-slate-900 mb-3">Medications</h2>
            <div className="space-y-3">
              {medications.map((med, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-900">{med.name}</span>
                    <span className="text-sm text-teal-600 font-medium">{med.dosage}</span>
                  </div>
                  {(med.purpose || med.notes) && (
                    <p className="text-sm text-slate-500 mt-1">{med.purpose || med.notes}</p>
                  )}
                  {med.changes && med.changes !== 'No changes' && (
                    <p className="text-sm text-amber-600 mt-1 font-medium">Change: {med.changes}</p>
                  )}
                  {med.importantNotes && (
                    <p className="text-sm text-red-600/80 mt-1">⚠ {med.importantNotes}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Follow up — rich format from backend */}
        {followUp && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100 space-y-4">
            <h2 className="font-semibold text-blue-900">Follow Up</h2>

            {followUp.nextAppointment && (
              <div>
                <p className="text-sm font-medium text-blue-700 mb-1">Next Appointment</p>
                <p className="text-blue-800">{followUp.nextAppointment}</p>
              </div>
            )}

            {followUp.actionItems?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-blue-700 mb-2">To-Do Before Next Visit</p>
                <ul className="space-y-1.5">
                  {followUp.actionItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-blue-800 text-sm">
                      <span className="text-blue-400 mt-0.5">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {followUp.warningSignals?.length > 0 && (
              <div className="bg-red-50 rounded-xl p-3 border border-red-100">
                <p className="text-sm font-medium text-red-700 mb-2">When to Call the Doctor</p>
                <ul className="space-y-1.5">
                  {followUp.warningSignals.map((signal, i) => (
                    <li key={i} className="flex items-start gap-2 text-red-800 text-sm">
                      <span className="text-red-400 mt-0.5">⚠</span>
                      {signal}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Follow up — legacy string format */}
        {followUpString && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100">
            <h2 className="font-semibold text-blue-900 mb-2">Follow Up</h2>
            <p className="text-blue-800">{followUpString}</p>
          </div>
        )}

        {/* Questions for next visit */}
        {questions.length > 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-100">
            <h2 className="font-semibold text-amber-900 mb-3">Questions for Next Visit</h2>
            <ul className="space-y-2">
              {questions.map((q, i) => (
                <li key={i} className="flex items-start gap-2 text-amber-800">
                  <span className="text-amber-500">•</span>
                  {q}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA — join Care Group (smart links by platform) */}
        {careGroup && (
          <SharedSummaryCTA careGroup={careGroup} patientName={patientName} shareId={shareId} />
        )}

        {/* Footer */}
        <div className="text-center pt-2 pb-8">
          <p className="text-xs text-slate-400">
            Powered by <span className="font-semibold">ClariMe Health</span>
          </p>
          <p className="text-xs text-slate-300 mt-1">
            This summary was shared securely. Only people with this link can view it.
          </p>
        </div>
      </div>
    </div>
  );
}
